import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { NamedLink } from '../../components';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './WedlyWeddingHero.module.css';

const CATEGORY_IDS = [
  'dress',
  'shoes',
  'decor',
  'accessory',
  'hens',
  'bridal-party',
  'groom',
  'mother-bride',
];

const THEME_DEFS = [
  {
    id: 'rustic',
    bg: '#F4EDE6',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="4" y="18" width="24" height="10" rx="2" fill="#C49A6C" />
        <rect x="10" y="10" width="12" height="10" rx="1" fill="#A0744A" />
        <circle cx="16" cy="6" r="4" fill="#6B9E6B" />
      </svg>
    ),
  },
  {
    id: 'elegant',
    bg: '#EEE9F2',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <ellipse cx="16" cy="16" rx="10" ry="12" fill="#D4B8D8" opacity="0.5" />
        <ellipse cx="16" cy="16" rx="6" ry="8" fill="#A882B0" />
        <circle cx="16" cy="16" r="3" fill="#7A5589" />
      </svg>
    ),
  },
  {
    id: 'beach',
    bg: '#E7F0F4',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path
          d="M4 22 Q10 14 16 18 Q22 22 28 14"
          stroke="#4A90B8"
          strokeWidth="1.5"
          fill="none"
        />
        <circle cx="24" cy="9" r="5" fill="#F5C842" opacity="0.8" />
        <path d="M4 26 H28" stroke="#A0C8D8" strokeWidth="2" />
      </svg>
    ),
  },
];

const HINT_IDS = ['WedlyHero.hint1', 'WedlyHero.hint2', 'WedlyHero.hint3'];

/** Message ids cycled in the hero search placeholder (typewriter, when input is empty). */
const PLACEHOLDER_ROTATION_IDS = [
  'WedlyHero.searchPlaceholder',
  'WedlyHero.searchPlaceholderRot2',
  'WedlyHero.searchPlaceholderRot3',
  'WedlyHero.searchPlaceholderRot4',
];

const FOOTER_LINKS = [
  { messageId: 'WedlyHero.footer.dresses', keywords: 'wedding dresses' },
  { messageId: 'WedlyHero.footer.venues', keywords: 'wedding venues' },
  { messageId: 'WedlyHero.footer.florals', keywords: 'wedding flowers' },
  { messageId: 'WedlyHero.footer.photography', keywords: 'wedding photography' },
  { messageId: 'WedlyHero.footer.catering', keywords: 'wedding catering' },
];

const SearchGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="4.5" stroke="#FDF8F5" strokeWidth="1.3" />
    <path d="M10.5 10.5L13 13" stroke="#FDF8F5" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

/**
 * Wedding-style marketing hero: search, category chips, theme cards, and footer shortcuts.
 * Matches the standalone WeddingHero design; optional embedded nav for use without the global topbar.
 *
 * @param {Object} props
 * @param {Function} props.onSearch — called with trimmed query string
 * @param {boolean} [props.showEmbeddedNav]
 * @param {string} [props.className]
 */
const WedlyWeddingHero = props => {
  const { onSearch, showEmbeddedNav = true, className } = props;
  const intl = useIntl();
  const [activeCategory, setActiveCategory] = useState('dress');
  const [query, setQuery] = useState('');
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');

  const placeholderPhrases = useMemo(
    () => PLACEHOLDER_ROTATION_IDS.map(id => intl.formatMessage({ id })),
    [intl]
  );

  const handleSearch = useCallback(() => {
    const q = query.trim();
    if (!q) {
      return;
    }
    onSearch?.(q);
  }, [onSearch, query]);

  const handleThemeClick = useCallback(
    themeId => {
      const themeLabel = intl.formatMessage({ id: `WedlyHero.theme.${themeId}` });
      const prompt = intl.formatMessage({ id: 'WedlyHero.themeSearchPrompt' }, { theme: themeLabel });
      onSearch?.(prompt);
    },
    [intl, onSearch]
  );

  const handleHintClick = useCallback(hintText => {
    setQuery(hintText);
  }, []);

  const staticSearchPlaceholder = intl.formatMessage({ id: 'WedlyHero.searchPlaceholder' });

  useEffect(() => {
    if (query.trim()) {
      setAnimatedPlaceholder('');
      return undefined;
    }

    const phrases = placeholderPhrases;
    if (!phrases.length) {
      return undefined;
    }

    let phraseIdx = 0;
    let charIdx = 0;
    let mode = 'forward';
    let pauseUntil = 0;
    const tickMs = 48;
    const holdMs = 2200;

    const step = () => {
      const now = Date.now();
      const target = phrases[phraseIdx];

      if (mode === 'pause') {
        if (now >= pauseUntil) {
          mode = 'backward';
        }
        return;
      }

      if (mode === 'forward') {
        if (charIdx < target.length) {
          charIdx += 1;
          setAnimatedPlaceholder(target.slice(0, charIdx));
        } else {
          mode = 'pause';
          pauseUntil = now + holdMs;
        }
        return;
      }

      if (charIdx > 0) {
        charIdx -= 1;
        setAnimatedPlaceholder(target.slice(0, charIdx));
      } else {
        phraseIdx = (phraseIdx + 1) % phrases.length;
        mode = 'forward';
        setAnimatedPlaceholder('');
      }
    };

    const id = setInterval(step, tickMs);
    step();
    return () => clearInterval(id);
  }, [query, placeholderPhrases]);

  return (
    <section
      className={classNames(css.root, className)}
      aria-labelledby="wedly-wedding-hero-title"
    >
      {showEmbeddedNav ? (
        <nav className={css.nav} aria-label={intl.formatMessage({ id: 'WedlyHero.navAria' })}>
          <button type="button" className={css.menuTrigger} aria-label={intl.formatMessage({ id: 'WedlyHero.openMenu' })}>
            {[0, 1, 2].map(i => (
              <span key={i} className={css.menuLine} />
            ))}
          </button>
          <div className={css.logoMark} aria-hidden="true">
            W
          </div>
          <ul className={css.navList}>
            {['inspiration', 'collections', 'vendors'].map(key => (
              <li key={key}>
                <NamedLink
                  name="SearchPage"
                  className={css.navLink}
                  to={{}}
                >
                  <FormattedMessage id={`WedlyHero.nav.${key}`} />
                </NamedLink>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}

      <div className={css.body}>
        <p className={css.kicker}>
          <FormattedMessage id="WedlyHero.kicker" />
        </p>

        <h1 id="wedly-wedding-hero-title" className={css.title}>
          <FormattedMessage id="WedlyHero.titleLine1" />
          <br />
          <span className={css.titleAccent}>
            <FormattedMessage id="WedlyHero.titleAccent" />
          </span>
        </h1>

        <p className={css.subtitle}>
          <FormattedMessage id="WedlyHero.subtitle" />
        </p>

        <div className={css.searchWrap}>
          <input
            id="wedly-hero-search"
            className={css.searchInput}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder={query.trim() ? staticSearchPlaceholder : animatedPlaceholder}
            autoComplete="off"
            aria-label={intl.formatMessage({ id: 'WedlyHero.searchLabel' })}
          />
          <button
            type="button"
            className={css.searchSubmit}
            onClick={handleSearch}
            aria-label={intl.formatMessage({ id: 'WedlyHero.searchSubmit' })}
          >
            <SearchGlyph />
          </button>
        </div>

        <div className={css.hintsRow}>
          <span className={css.hintsLabel}>
            <FormattedMessage id="WedlyHero.tryLabel" />
          </span>
          {HINT_IDS.map(msgId => (
            <button
              key={msgId}
              type="button"
              className={css.hintBtn}
              onClick={() => handleHintClick(intl.formatMessage({ id: msgId }))}
            >
              &ldquo;
              <FormattedMessage id={msgId} />
              &rdquo;
            </button>
          ))}
        </div>

        <p className={css.sectionLabel}>
          <FormattedMessage id="WedlyHero.shopByCategory" />
        </p>
        <div className={css.chipRow}>
          {CATEGORY_IDS.map(catId => (
            <button
              key={catId}
              type="button"
              className={classNames(css.chip, {
                [css.chipActive]: activeCategory === catId,
              })}
              onClick={() => setActiveCategory(catId)}
            >
              <FormattedMessage id={`WedlyHero.category.${catId}`} />
            </button>
          ))}
        </div>

        <div className={css.divider} role="presentation" />

        <p className={css.sectionLabel}>
          <FormattedMessage id="WedlyHero.browseThemes" />
        </p>
        <div className={css.themeGrid}>
          {THEME_DEFS.map(theme => (
            <button
              key={theme.id}
              type="button"
              className={css.themeCard}
              onClick={() => handleThemeClick(theme.id)}
            >
              <div className={css.themeSwatch} style={{ backgroundColor: theme.bg }}>
                {theme.icon}
              </div>
              <div className={css.themeLabel}>
                <FormattedMessage id={`WedlyHero.theme.${theme.id}`} />
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className={css.footerBar}>
        {FOOTER_LINKS.map((item, i) => (
          <div key={item.messageId} className={css.footerItem}>
            <NamedLink
              name="SearchPage"
              className={css.footerLink}
              to={{ search: `?keywords=${encodeURIComponent(item.keywords)}` }}
            >
              <FormattedMessage id={item.messageId} />
            </NamedLink>
            {i < FOOTER_LINKS.length - 1 ? <span className={css.footerDot} aria-hidden="true" /> : null}
          </div>
        ))}
      </div>
    </section>
  );
};

WedlyWeddingHero.propTypes = {
  onSearch: PropTypes.func,
  showEmbeddedNav: PropTypes.bool,
  className: PropTypes.string,
};

export default WedlyWeddingHero;
